import { BUILDINGS } from '../../data/buildings.js';
import { CAMP_AGGRO_RADIUS_PX, CAMP_LEASH_RADIUS_PX } from '../../data/camps.js';
import {
  DISRUPT_UPLINK_FIRE_RATE_MULT,
  DISRUPT_UPLINK_RANGE_MULT,
} from '../../data/disruptUplink.js';
import { BULWARK_TURRET_RANGE_MULT, VANGUARD_REGEN_PER_SEC } from '../../data/doctrines.js';
import { FORTIFY_RELAY_TURRET_FIRE_RATE_MULT } from '../../data/fortifyRelay.js';
import { FORWARD_TURRET_RANGE_SQ } from '../../data/forwardTurret.js';
import {
  SIGNAL_STORM_TURRET_FIRE_RATE_MULT,
  SIGNAL_STORM_TURRET_RANGE_MULT,
} from '../../data/signalStorm.js';
import { SQUAD_DEFS } from '../../data/squads.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { MiniTurret, Turret } from '../entities/Building.js';
import { isOperational } from '../entities/Building.js';
import type { NeutralUnit } from '../entities/NeutralCamp.js';
import type { Projectile, ProjectileTarget } from '../entities/Projectile.js';
import type { Squad, SquadTargetRef } from '../entities/Squad.js';
import type { EntityId, PlayerId } from '../ids.js';
import { entityId } from '../ids.js';

import { routeDamage } from './damageRouting.js';

// CORE_ARMOR_SCALING_* constants live in damageRouting.ts (the module that owns
// the damage path). Re-exported here so existing imports keep working.
export {
  CORE_ARMOR_SCALING_THRESHOLD,
  CORE_ARMOR_SCALING_FACTOR_ABOVE,
  CORE_ARMOR_SCALING_FACTOR_BELOW,
} from './damageRouting.js';

// CombatSystem responsibilities, per tick:
//   1. Decrement attack cooldowns; re-acquire each squad's nearest enemy target
//      (squads, cores, buildings).
//   2. If target is in firing range and cooldown ready, spawn a Projectile and
//      set the next cooldown.
//   3. For each operational turret: same loop but only acquire enemy squads
//      (turrets are anti-infantry per docs/prompts/04_production_buildings.md).
//   4. Advance projectiles; on TTL expiry apply damage to the recorded target.
//   5. Sync `members[].alive` to the squad's hp pool and reap squads at hp 0.
//
// Cores receive damage but are NOT removed when their hp reaches 0 — the win
// condition for Core destruction is owned by WinConditionSystem. Buildings die
// when their hp reaches 0 — BuildingSystem reaps them.
//
// Acquisition is O(actors × (squads + cores + buildings)) per tick; trivially
// small at the V0.4 cap of 4 squads per side and ≤12 buildings.
export function combatSystem(state: MatchState): void {
  acquireAndFireSquads(state);
  acquireAndFireTurrets(state);
  acquireAndFireNeutralUnits(state);
  advanceProjectiles(state);
  reapDeadSquads(state);
  applyVanguardRegen(state);
}

function acquireAndFireSquads(state: MatchState): void {
  for (const squad of Object.values(state.squads)) {
    if (squad.attackCooldown > 0) {
      squad.attackCooldown = Math.max(0, squad.attackCooldown - SECONDS_PER_TICK);
    }
    // V1.5+ Slice 2 — channeling squads (currently only Recon mid-Cut Link)
    // hold fire for the channel duration. Clear the engaged target so the HUD
    // doesn't show a misleading "engaging X" while the squad sits still.
    if (squad.channelingAbility !== null) {
      squad.target = null;
      continue;
    }
    const def = SQUAD_DEFS[squad.archetype];
    const target = pickNearestSquadTarget(state, squad);
    squad.target = target;
    if (target === null) continue;
    const targetPos = readTargetPos(state, target);
    if (targetPos === null) {
      squad.target = null;
      continue;
    }
    const distSq = sqDist(squad.pos, targetPos);
    if (distSq > def.range * def.range) continue;
    if (squad.attackCooldown > 0) continue;
    fireSquadProjectile(state, squad, target, targetPos);
    squad.attackCooldown = def.attackCooldownSec;
  }
}

function acquireAndFireTurrets(state: MatchState): void {
  for (const b of Object.values(state.buildings)) {
    if (b.kind !== 'turret' && b.kind !== 'mini_turret') continue;
    if (!isOperational(b)) continue;
    // V1.5+ forward turret network dependency. Offline mini-turrets don't
    // fire — that's the dramatic battlefield consequence
    // docs/02_GAME_DESIGN.md §15 specifies. Permanent turrets are kept
    // always-on by ForwardTurretNetworkSystem (Core-ring anchor) so the
    // guard is effectively mini_turret-only.
    //
    // Composition with V1.5+ Slice 6 (Disrupt Uplink): isOnline is binary
    // (Cut Link's lever); disrupted is analog (this slice's lever). A
    // turret that's both offline AND disrupted does not fire — Cut Link
    // wins because off > slow. Disrupt Uplink only matters when the
    // turret is online.
    if (!b.isOnline) continue;
    if (b.attackCooldown > 0) {
      b.attackCooldown = Math.max(0, b.attackCooldown - SECONDS_PER_TICK);
    }
    const def = BUILDINGS[b.kind];
    // V1.5 Bulwark: turrets owned by a Bulwark player fire at +25% range. The
    // multiplier covers both acquisition (in pickNearestSquadOnly) and the
    // firing-distance gate below.
    const rangeMult = turretRangeMultFor(state, b.ownerId);
    // V1.5+ Slice 6 — Disrupt Uplink: if any anchored ControlNode is in a
    // disruption window applied by the OPPOSITE player, scale fire rate
    // and range. Multiplier is applied AFTER the Bulwark range mult, so
    // the disruption stacks correctly: a Bulwark turret on a disrupted
    // relay still feels weakened, just from a higher baseline.
    const disrupted = b.kind === 'mini_turret' && isMiniTurretDisrupted(state, b);
    // V1.5+ Slice 7 — Fortify Relay: the defensive dual of Disrupt. If any
    // anchored ControlNode is in a fortify window applied by THIS turret's
    // owner, scale fire rate UP by FORTIFY_RELAY_TURRET_FIRE_RATE_MULT.
    // Range is NOT modified (range is the disrupt-specific lever). Disrupt
    // and Fortify CAN apply to the same turret simultaneously (different
    // anchored relays); they compose multiplicatively on the cooldown
    // factor below. Same-relay both-applied is impossible because Fortify
    // makes the relay immune to incoming Disrupt.
    const fortified = b.kind === 'mini_turret' && isMiniTurretFortified(state, b);
    // V1.5+ Slice 8 — Signal Storm Endgame: during 7:00–8:00 every
    // forward `mini_turret` (BOTH sides, regardless of disrupt/fortify
    // state) fires at SIGNAL_STORM_TURRET_FIRE_RATE_MULT × the
    // un-stormed rate and at SIGNAL_STORM_TURRET_RANGE_MULT × the
    // un-stormed range. Permanent `turret` buildings (Core-ring) are
    // unaffected — the storm targets FORWARD firepower so a player who
    // never pushed a turret out of base isn't punished. Composes
    // multiplicatively with disrupt + fortify on the cooldown factor;
    // the storm is the OUTERMOST multiplier (applied last).
    const stormed = b.kind === 'mini_turret' && isMatchInSignalStorm(state);
    let effectiveRangeMult = rangeMult;
    if (disrupted) effectiveRangeMult *= DISRUPT_UPLINK_RANGE_MULT;
    if (stormed) effectiveRangeMult *= SIGNAL_STORM_TURRET_RANGE_MULT;
    const range = def.range * effectiveRangeMult;
    const target = pickNearestSquadOnly(state, b, range);
    b.target = target;
    if (target === null) continue;
    // pickNearestSquadOnly returns squad OR neutral_unit refs — the comment
    // on the neutralUnits loop above says turrets engage camp guards too.
    // The previous lookup only checked state.squads, so a guard-kind target
    // silently nulled out and the turret never fired. readTargetPos
    // dispatches on ref.kind correctly.
    const targetPos = readTargetPos(state, target);
    if (targetPos === null) {
      b.target = null;
      continue;
    }
    const distSq = sqDist(b.pos, targetPos);
    if (distSq > range * range) continue;
    if (b.attackCooldown > 0) continue;
    fireTurretProjectile(state, b, target, targetPos);
    // Fire-rate degradation: when disrupted, the cooldown reset is scaled
    // by 1 / FIRE_RATE_MULT, so a 0.6× rate becomes a 1.667× cooldown
    // (turret fires 60% as often as normal). Math.imul-equivalent works
    // here because cooldown is a float; we just multiply.
    //
    // Fire-rate buff: when fortified, the cooldown reset is scaled by
    // 1 / FORTIFY_FIRE_RATE_MULT (1/1.25 = 0.8), so a 1.25× rate becomes
    // a 0.8× cooldown (turret fires 25% more often). Composes
    // multiplicatively with disrupt on the cooldown factor: a turret that
    // is BOTH disrupted (different relay) and fortified (this relay) ends
    // up at 0.6 × 1.25 = 0.75× rate (≈1.333× cooldown).
    let cooldownMult = 1;
    if (disrupted) cooldownMult *= 1 / DISRUPT_UPLINK_FIRE_RATE_MULT;
    if (fortified) cooldownMult *= 1 / FORTIFY_RELAY_TURRET_FIRE_RATE_MULT;
    if (stormed) cooldownMult *= 1 / SIGNAL_STORM_TURRET_FIRE_RATE_MULT;
    b.attackCooldown = def.attackCooldownSec * cooldownMult;
  }
}

// V1.5+ Slice 8 — true if the match is currently in the Signal Storm
// phase. Cheap O(1) check; centralised so other call-sites can reuse the
// same predicate (capture-speed acceleration in CaptureSystem reads the
// same flag).
function isMatchInSignalStorm(state: MatchState): boolean {
  return state.phase === 'signal_storm';
}

// Owner-keyed lookup so a turret's range is always read from its owner's chosen
// doctrine. Mini-turrets (Engineer ability spawn) are also owned by a player and
// thus also benefit from Bulwark — the spec calls this out explicitly.
function turretRangeMultFor(state: MatchState, ownerId: PlayerId): number {
  return state.players[ownerId].doctrineId === 'bulwark' ? BULWARK_TURRET_RANGE_MULT : 1;
}

// V1.5+ Slice 6 — true if any ControlNode within FORWARD_TURRET_RANGE_PX of
// the mini-turret is in a disruption window AND the disruptor is the
// OPPOSITE player from the turret's owner. Self-disrupts (the disruptor is
// the turret's own player) are filtered out — they would represent a
// tactical mistake, but the effect should not punish the player for it.
// O(controlNodes) per turret per tick; trivial at the V1.5 map's 3 relays.
function isMiniTurretDisrupted(state: MatchState, turret: MiniTurret): boolean {
  const tick = state.tick;
  for (const node of Object.values(state.controlNodes)) {
    if (node.disruptedByPlayerId === null) continue;
    if (node.disruptedByPlayerId === turret.ownerId) continue;
    if (tick >= node.disruptedUntilTick) continue;
    const dx = node.pos.x - turret.pos.x;
    const dy = node.pos.y - turret.pos.y;
    if (dx * dx + dy * dy <= FORWARD_TURRET_RANGE_SQ) return true;
  }
  return false;
}

// V1.5+ Slice 7 — true if any ControlNode within FORWARD_TURRET_RANGE_PX of
// the mini-turret is in a fortify window AND the fortifier is the SAME
// player as the turret's owner. Inverse condition of isMiniTurretDisrupted:
// fortify is a defensive buff for the relay owner, so it only applies to
// turrets owned by the fortifier. Cross-player fortifies (Shield A
// fortifies a relay where Player B has dropped a mini-turret) have no
// fire-rate effect — only the immunity property does work.
function isMiniTurretFortified(state: MatchState, turret: MiniTurret): boolean {
  const tick = state.tick;
  for (const node of Object.values(state.controlNodes)) {
    if (node.fortifiedByPlayerId === null) continue;
    if (node.fortifiedByPlayerId !== turret.ownerId) continue;
    if (tick >= node.fortifiedUntilTick) continue;
    const dx = node.pos.x - turret.pos.x;
    const dy = node.pos.y - turret.pos.y;
    if (dx * dx + dy * dy <= FORWARD_TURRET_RANGE_SQ) return true;
  }
  return false;
}

// Acquire even outside firing range so the selected-squad UI can hint at the
// engaged enemy; firing is gated to def.range. Cap acquisition at 2× firing
// range so distant cores don't override a closer enemy in dense fights.
function pickNearestSquadTarget(state: MatchState, squad: Squad): SquadTargetRef | null {
  const def = SQUAD_DEFS[squad.archetype];
  const acquireRange = def.range * 2;
  const acquireSq = acquireRange * acquireRange;
  let best: SquadTargetRef | null = null;
  let bestSq = Infinity;
  for (const other of Object.values(state.squads)) {
    if (other.ownerId === squad.ownerId) continue;
    const dSq = sqDist(squad.pos, other.pos);
    if (dSq > acquireSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { kind: 'squad', id: other.id };
    }
  }
  for (const core of Object.values(state.cores)) {
    if (core.ownerId === squad.ownerId) continue;
    const dSq = sqDist(squad.pos, core.pos);
    if (dSq > acquireSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { kind: 'core', id: core.id };
    }
  }
  for (const b of Object.values(state.buildings)) {
    if (b.ownerId === squad.ownerId) continue;
    const dSq = sqDist(squad.pos, b.pos);
    if (dSq > acquireSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { kind: 'building', id: b.id };
    }
  }
  // V1.5 neutral camp guards are hostile to both players; player squads target
  // them like any enemy squad.
  for (const u of Object.values(state.neutralUnits)) {
    const dSq = sqDist(squad.pos, u.pos);
    if (dSq > acquireSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { kind: 'neutral_unit', id: u.id };
    }
  }
  return best;
}

function pickNearestSquadOnly(
  state: MatchState,
  turret: Turret | MiniTurret,
  effectiveRange: number,
): SquadTargetRef | null {
  // Acquire out to 2× the turret's effective firing range so the UI/target
  // hint can see the leading enemy even before it walks into firing range.
  const acquireRange = effectiveRange * 2;
  const acquireSq = acquireRange * acquireRange;
  let best: SquadTargetRef | null = null;
  let bestSq = Infinity;
  for (const sq of Object.values(state.squads)) {
    if (sq.ownerId === turret.ownerId) continue;
    const dSq = sqDist(turret.pos, sq.pos);
    if (dSq > acquireSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { kind: 'squad', id: sq.id };
    }
  }
  // Player turrets also engage neutral camp guards if they're in range.
  for (const u of Object.values(state.neutralUnits)) {
    const dSq = sqDist(turret.pos, u.pos);
    if (dSq > acquireSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = { kind: 'neutral_unit', id: u.id };
    }
  }
  return best;
}

export function readTargetPos(
  state: MatchState,
  ref: SquadTargetRef,
): { x: number; y: number } | null {
  if (ref.kind === 'squad') {
    const sq = state.squads[ref.id];
    return sq === undefined ? null : sq.pos;
  }
  if (ref.kind === 'core') {
    const core = state.cores[ref.id];
    return core === undefined ? null : core.pos;
  }
  if (ref.kind === 'neutral_unit') {
    const u = state.neutralUnits[ref.id];
    return u === undefined ? null : u.pos;
  }
  const b = state.buildings[ref.id];
  return b === undefined ? null : b.pos;
}

function fireSquadProjectile(
  state: MatchState,
  squad: Squad,
  target: SquadTargetRef,
  targetPos: { x: number; y: number },
): void {
  const def = SQUAD_DEFS[squad.archetype];
  const proj = makeProjectile(state, {
    ownerId: squad.ownerId,
    fromSquadId: squad.id,
    fromBuildingId: null,
    fromPos: squad.pos,
    targetPos,
    target: refToProjectileTarget(target),
    speed: def.projectileSpeed,
    damage: def.damagePerShot,
  });
  state.projectiles[proj.id] = proj;
  squad.facing = Math.atan2(targetPos.y - squad.pos.y, targetPos.x - squad.pos.x);
}

function fireTurretProjectile(
  state: MatchState,
  turret: Turret | MiniTurret,
  target: SquadTargetRef,
  targetPos: { x: number; y: number },
): void {
  const def = BUILDINGS[turret.kind];
  const proj = makeProjectile(state, {
    ownerId: turret.ownerId,
    fromSquadId: null,
    fromBuildingId: turret.id,
    fromPos: turret.pos,
    targetPos,
    target: refToProjectileTarget(target),
    speed: def.projectileSpeed,
    damage: def.damagePerShot,
  });
  state.projectiles[proj.id] = proj;
  turret.facing = Math.atan2(targetPos.y - turret.pos.y, targetPos.x - turret.pos.x);
}

function makeProjectile(
  state: MatchState,
  args: {
    ownerId: PlayerId;
    fromSquadId: EntityId | null;
    fromBuildingId: EntityId | null;
    fromPos: { x: number; y: number };
    targetPos: { x: number; y: number };
    target: ProjectileTarget;
    speed: number;
    damage: number;
  },
): Projectile {
  const dx = args.targetPos.x - args.fromPos.x;
  const dy = args.targetPos.y - args.fromPos.y;
  const dist = Math.hypot(dx, dy);
  const inv = dist > 0 ? 1 / dist : 0;
  const vx = dx * inv * args.speed;
  const vy = dy * inv * args.speed;
  // TTL == flight time. Damage applies on TTL expiry — no missed shots in V0.x.
  const flightSec = dist > 0 ? dist / args.speed : 0;
  const ttl = Math.max(1, Math.ceil(flightSec / SECONDS_PER_TICK));
  const id = mintEntityId(state);
  return {
    id,
    ownerId: args.ownerId,
    fromSquadId: args.fromSquadId,
    fromBuildingId: args.fromBuildingId,
    fromNeutralUnitId: null,
    pos: { x: args.fromPos.x, y: args.fromPos.y },
    velocity: { x: vx, y: vy },
    damage: args.damage,
    ttl,
    target: args.target,
  };
}

function refToProjectileTarget(ref: SquadTargetRef): ProjectileTarget {
  if (ref.kind === 'squad') return { kind: 'squad', id: ref.id };
  if (ref.kind === 'core') return { kind: 'core', id: ref.id };
  if (ref.kind === 'neutral_unit') return { kind: 'neutral_unit', id: ref.id };
  return { kind: 'building', id: ref.id };
}

function advanceProjectiles(state: MatchState): void {
  const expired: EntityId[] = [];
  for (const proj of Object.values(state.projectiles)) {
    proj.pos.x += proj.velocity.x * SECONDS_PER_TICK;
    proj.pos.y += proj.velocity.y * SECONDS_PER_TICK;
    proj.ttl -= 1;
    if (proj.ttl > 0) continue;
    // Apply damage if the target still exists. If it died mid-flight the shot is wasted.
    if (projectileTargetExists(state, proj.target)) {
      routeDamage(state, proj.target, proj.damage, proj.ownerId);
    }
    expired.push(proj.id);
  }
  for (const id of expired) delete state.projectiles[id];
}

function projectileTargetExists(state: MatchState, t: ProjectileTarget): boolean {
  if (t.kind === 'squad') return state.squads[t.id] !== undefined;
  if (t.kind === 'core') return state.cores[t.id] !== undefined;
  if (t.kind === 'neutral_unit') return state.neutralUnits[t.id] !== undefined;
  return state.buildings[t.id] !== undefined;
}

// Neutral camp guards aggro on player squads and shoot back. Aggro acquires
// any player squad within CAMP_AGGRO_RADIUS_PX of the unit; once aggroed, the
// unit pursues until the target dies, leaves CAMP_LEASH_RADIUS_PX, or the
// unit itself dies. Movement (the chase part) is a small per-tick step toward
// the target — we don't path through obstacles, the camps live in open
// terrain at mid by design.
function acquireAndFireNeutralUnits(state: MatchState): void {
  for (const u of Object.values(state.neutralUnits)) {
    if (u.attackCooldown > 0) {
      u.attackCooldown = Math.max(0, u.attackCooldown - SECONDS_PER_TICK);
    }
    // Re-check / re-acquire aggro target.
    const target = pickAggroTarget(state, u);
    u.aggroTargetId = target === null ? null : target.id;
    if (target === null) continue;
    const dSq = sqDist(u.pos, target.pos);
    if (dSq > u.range * u.range) continue;
    if (u.attackCooldown > 0) continue;
    fireNeutralProjectile(state, u, target);
    u.attackCooldown = u.attackCooldownSec;
  }
}

function pickAggroTarget(state: MatchState, unit: NeutralUnit): Squad | null {
  // If we have a current aggro target and it's still within leash, keep it.
  if (unit.aggroTargetId !== null) {
    const current = state.squads[unit.aggroTargetId];
    if (
      current !== undefined &&
      sqDist(unit.pos, current.pos) <= CAMP_LEASH_RADIUS_PX * CAMP_LEASH_RADIUS_PX
    ) {
      return current;
    }
  }
  // Otherwise re-acquire any player squad inside aggro radius.
  const aggroSq = CAMP_AGGRO_RADIUS_PX * CAMP_AGGRO_RADIUS_PX;
  let best: Squad | null = null;
  let bestSq = Infinity;
  for (const sq of Object.values(state.squads)) {
    const dSq = sqDist(unit.pos, sq.pos);
    if (dSq > aggroSq) continue;
    if (dSq < bestSq) {
      bestSq = dSq;
      best = sq;
    }
  }
  return best;
}

function fireNeutralProjectile(state: MatchState, unit: NeutralUnit, target: Squad): void {
  const dx = target.pos.x - unit.pos.x;
  const dy = target.pos.y - unit.pos.y;
  const dist = Math.hypot(dx, dy);
  const inv = dist > 0 ? 1 / dist : 0;
  const vx = dx * inv * unit.projectileSpeed;
  const vy = dy * inv * unit.projectileSpeed;
  const flightSec = dist > 0 ? dist / unit.projectileSpeed : 0;
  const ttl = Math.max(1, Math.ceil(flightSec / SECONDS_PER_TICK));
  const id = mintEntityId(state);
  // Neutral projectiles have no PlayerId owner — we use sentinel 0 since
  // routeDamage's mark/Phase Shield logic only applies when the squad target
  // is owned by the bubble's owner; neutral damage will not match either
  // bubble. The squad target itself is the player-owned squad, so the bubble
  // check still works correctly via sq.ownerId.
  state.projectiles[id] = {
    id,
    ownerId: null,
    fromSquadId: null,
    fromBuildingId: null,
    fromNeutralUnitId: unit.id,
    pos: { x: unit.pos.x, y: unit.pos.y },
    velocity: { x: vx, y: vy },
    damage: unit.damagePerShot,
    ttl,
    target: { kind: 'squad', id: target.id },
  };
  unit.facing = Math.atan2(dy, dx);
}

function reapDeadSquads(state: MatchState): void {
  const dead: EntityId[] = [];
  for (const sq of Object.values(state.squads)) {
    if (sq.hp <= 0) dead.push(sq.id);
  }
  for (const id of dead) {
    const sq = state.squads[id];
    if (sq === undefined) continue;
    decrementOwnerSquadCount(state, sq.ownerId);
    delete state.squads[id];
  }
}

function decrementOwnerSquadCount(state: MatchState, ownerId: PlayerId): void {
  const player = state.players[ownerId];
  player.squadCount = Math.max(0, player.squadCount - 1);
}

// V1.5 Vanguard: each Vanguard player's squads regen VANGUARD_REGEN_PER_SEC of
// maxHp / second while out of combat. "Out of combat" = squad has no current
// target AND its attackCooldown has fully drained. We check both because target
// can be re-acquired immediately while cooldown is still draining; using both
// gates avoids regen-while-firing edge cases.
function applyVanguardRegen(state: MatchState): void {
  const heal0 = state.players[0].doctrineId === 'vanguard';
  const heal1 = state.players[1].doctrineId === 'vanguard';
  if (!heal0 && !heal1) return;
  for (const sq of Object.values(state.squads)) {
    const enabled = sq.ownerId === 0 ? heal0 : heal1;
    if (!enabled) continue;
    if (sq.target !== null) continue;
    if (sq.attackCooldown > 0) continue;
    if (sq.hp <= 0) continue;
    if (sq.hp >= sq.maxHp) continue;
    const heal = VANGUARD_REGEN_PER_SEC * sq.maxHp * SECONDS_PER_TICK;
    sq.hp = Math.min(sq.maxHp, sq.hp + heal);
    healSyncMembers(sq);
  }
}

// Mirror of syncMembersToHp but for the heal direction — re-flag any member that
// would now be alive given the higher hp. Ordered low-index-first for visual
// consistency with how members die (high-index dies first in syncMembersToHp).
function healSyncMembers(squad: Squad): void {
  const n = squad.members.length;
  if (n === 0) return;
  const aliveTarget = squad.hp <= 0 ? 0 : Math.max(1, Math.ceil((n * squad.hp) / squad.maxHp));
  let alive = 0;
  for (const m of squad.members) if (m.alive) alive++;
  if (alive >= aliveTarget) return;
  let toRevive = aliveTarget - alive;
  for (let i = 0; i < squad.members.length && toRevive > 0; i++) {
    const member = squad.members[i];
    if (member !== undefined && !member.alive) {
      member.alive = true;
      toRevive--;
    }
  }
}

function sqDist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function mintEntityId(state: MatchState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return entityId(id);
}
